import React, { useEffect, useState } from 'react';
import { GatsbyImage, getImage, IGatsbyImageData } from 'gatsby-plugin-image';
import { useLayoutContext } from '../../providers/LayoutProvider';
import {
    filterArrayOfObjByKey,
    isURLValid,
    queryBuilder,
} from '../../shared/Utils';
import { DOMParseHTMLElement, stripHtml, stripHTMLStrings } from '../../shared/stripHtml';
import { AccentedText } from '../AccentedText/AccentedText';
import { ThemedIcon, IFormIcons } from '../ThemedIcon/ThemedIcon';
import './TwoColumnFormText.scss';
import '../QuoteText/QuoteText.scss';

export interface ITwoColumnFormText {
    theme: `white` | `gray` | `dark`;
    formHeight: number;
    embeddedCode: string;
    contextCol: ITwoColumnFormTextContext;
}

export interface ITwoColumnFormTextContext {
    eyebrow?: string;
    heading?: string;
    headingOption: boolean;
    contextCopy?: string;
    contextMediaData?: IContextMedia;
    formIcons?: IFormIcons[];
    formQuotes?: IFormQuotes[];
    conclusion: string;
}

interface IContextMedia {
    contextMedia?: IGatsbyImageData;
    contextMediaMobile: boolean;
    contextMediaAlt?: string;
}

interface IFormQuotes {
    speaker?: string;
    quoteText?: string;
}

export const TwoColumnFormText = (props: ITwoColumnFormText) => {
    if (!props.embeddedCode) return null;
    const {
        eyebrow,
        heading,
        headingOption,
        contextCopy,
        contextMediaData,
        formIcons,
        formQuotes,
        conclusion,
    } = props.contextCol;
    const { campaignTermsFilter } = useLayoutContext();
    const [queryString, setQueryString] = useState(``);
    const [srcString, setSrcString] = useState<string>(null);
    const filteredQuotes: IFormQuotes[] = filterArrayOfObjByKey(
        formQuotes,
        `quoteText`
    );
    const filteredIcons: IFormIcons[] = filterArrayOfObjByKey(
        formIcons,
        `iconText`
    );
    const queryParametersToString = queryBuilder(campaignTermsFilter, false);

    const HeadingEl = ({ children }) => {
        const headingChildren = children.filter((child) => child);
        if (headingChildren.length <= 0) return null;
        if (headingOption) {
            return <h1>{headingChildren}</h1>;
        } else {
            return <h2>{headingChildren}</h2>;
        }
    };

    useEffect(() => {
        setQueryString(
            `${!queryParametersToString ? `` : queryParametersToString}`
        );
    }, [queryParametersToString]);

    useEffect(() => {
        if (typeof window !== `undefined`) {
            const parsedSrc = DOMParseHTMLElement(
                props.embeddedCode
            )?.querySelector(`iframe`)?.src;
            setSrcString(parsedSrc);
        }
    }, []);

    const IframeColComponent = () => {
        if (props.embeddedCode) {
            return (
                <div
                    className={`form-iframe-column ${props.theme?.toLowerCase()}`}
                >
                    <p className="typestack-caption">
                        All fields are required.
                    </p>
                    {isURLValid(srcString) && (
                        <div className="iframe-container">
                            <iframe
                                data-id="external-form"
                                src={`${srcString}${queryString}`}
                                frameBorder="0"
                                style={{
                                    border: 0,
                                    width: `100%`,
                                    height: props.formHeight
                                        ? `${props.formHeight}px`
                                        : ``,
                                }}
                                allow="transparency"
                                title={`${heading} form`}
                            ></iframe>
                        </div>
                    )}
                </div>
            );
        }
    };

    return (
        <section
            className={`themed-container tester-a c--two-column-form-text ${props.theme?.toLowerCase()}`}
        >
            <div className="kite-container">
                <div id={`formContextColumn`} className={`form-context-column`}>
                    {(heading || eyebrow || contextCopy) && (
                        <div className="c--text-content">
                            <HeadingEl>
                                {eyebrow && (
                                    <span className="eyebrow">{eyebrow}</span>
                                )}
                                {heading && (
                                    <span>
                                        <AccentedText>{heading}</AccentedText>
                                    </span>
                                )}
                            </HeadingEl>
                            {contextCopy && (
                                <div
                                    className="body"
                                    dangerouslySetInnerHTML={{
                                        __html: contextCopy,
                                    }}
                                />
                            )}
                        </div>
                    )}

                    {contextMediaData?.contextMedia && (
                        <GatsbyImage
                            image={getImage(contextMediaData.contextMedia)}
                            alt={contextMediaData.contextMediaAlt ?? ``}
                            className={`${
                                contextMediaData?.contextMediaMobile
                                    ? ``
                                    : `mobile-disabled`
                            }`}
                        />
                    )}

                    {filteredIcons?.length > 0 && (
                        <ul className={`form-icons`}>
                            {filteredIcons.map(
                                ({ iconAltTag, iconText, iconImg }, idx) => (
                                    <ThemedIcon
                                        key={idx}
                                        iconAltTag={iconAltTag}
                                        iconText={iconText}
                                        iconImg={iconImg}
                                        background={props.theme}
                                    />
                                )
                            )}
                        </ul>
                    )}

                    {filteredQuotes?.length > 0 && (
                        <div className={`quote-content`}>
                            {filteredQuotes.map((quote, idx) => (
                                <figure key={idx}>
                                    {quote?.quoteText && (
                                        <blockquote
                                            className="typestack-title-1"
                                            dangerouslySetInnerHTML={{
                                                __html: quote?.quoteText,
                                            }}
                                        />
                                    )}
                                    {quote?.speaker && (
                                        <figcaption className="author typestack-body-1">
                                            - {quote?.speaker ?? `Anonymous`}
                                        </figcaption>
                                    )}
                                </figure>
                            ))}
                        </div>
                    )}

                    {conclusion && (
                        <div className={`conclusion`}>
                            <p
                                className="typestack-body-1"
                                dangerouslySetInnerHTML={{
                                    __html: stripHtml(conclusion),
                                }}
                            ></p>
                        </div>
                    )}
                </div>
                {srcString && isURLValid(srcString) && <IframeColComponent />}
            </div>
        </section>
    );
};
